import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {formatStringForCsv} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {downloadBlob} from '../../../../analytics/components/exports/helpers/metricExports';
import ruleEditorEmptyTableIllustration from '../../../../assets/images/ruleEditorEmptyTable.svg';
import {LearnMoreButton} from '../../../../components/common/callToAction/learnMoreButton';
import {DataFieldsTable, DataFieldsTableProps} from '../../../../components/dataTable/dataFieldsTable';
import {
  DataFieldsTableUpdater,
  useDataFieldsTableActions,
} from '../../../../components/dataTable/useDataFieldsTableActions';
import {
  brandDataFieldsTableColumnId,
  DataFieldsTableColumnModel,
  DataFieldsTableColumnTypesEnum,
  DataFieldsTableData,
  DataFieldsTableModel,
} from '../../../../models/api/dataFieldsTableModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {SettingsSearchInput} from '../../../common/settingsSearchInput';

/*
 * Props.
 */

interface RuleEditorDataFieldsTableProps {
  table: DataFieldsTableModel;
  onUpdate: HandlerOf<DataFieldsTableUpdater>;
  trackingData?: Record<string, unknown>;
}

/*
 * Styles.
 */

const StyledEmptyStateWrapper = styled.div`
  margin: auto;
  ${flexCenter};
  flex-flow: column;
  max-width: 310px;
`;

const StyledEmptyStateTitle = styled.h2`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledEmptyStateDescription = styled.p`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 150%;
  color: ${(p) => p.theme.greys.shade70};

  text-align: center;
`;

const StyledEmptyStateButtonsWrapper = styled.div`
  ${flexCenter};
  > :not(:first-child) {
    margin-left: 8px;
  }
`;

const StyledWrapper = styled.div`
  display: grid;
  height: 100%;
  grid-auto-flow: row;
  grid-template-rows: 30px 1fr;
  grid-gap: 34px;
`;

const StyledTopBar = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: 350px min-content;
  align-content: center;
  justify-content: space-between;
`;

/*
 * Component.
 */

export const RuleEditorDataFieldsTable: FC<RuleEditorDataFieldsTableProps> = ({
  table,
  onUpdate,
  trackingData,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');

  const {onAddRow, onAddColumn, ...tableActions} = useDataFieldsTableActions(onUpdate);

  const onExportCsv = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_EXPORT, trackingData);

    exportCsv(table);
  }, [table, trackingData]);

  const onImportCsv = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_IMPORT, trackingData);

    importCsv(onUpdate);
  }, [onUpdate, trackingData]);

  const onAddFirstRow = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_FIRST_NEW_ROW, trackingData);

    onAddRow();
  }, [onAddRow, trackingData]);

  const onAddRowWithTracking = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_ADD_ROW, trackingData);

    onAddRow();
  }, [onAddRow, trackingData]);

  const onAddColumnWithTracking = useCallback<DataFieldsTableProps['onAddColumn']>(
    (data) => {
      trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_ADD_COLUMN, trackingData);

      onAddColumn(data);
    },
    [onAddColumn, trackingData],
  );

  const columnValidator = useCallback<DataFieldsTableProps['columnValidator']>(
    ({name, id}) => {
      if (name !== undefined && name.length <= 0) {
        return <Trans id="data-fields-table-column-validator-too-short">Column name too short.</Trans>;
      }
      if (table.columns.some((column) => column.name === name && column.id !== id)) {
        return <Trans id="data-fields-table-column-validator-exists">Column name already exists.</Trans>;
      }

      return null;
    },
    [table.columns],
  );

  const emptyState = useMemo(
    () => makeEmptyState(onAddFirstRow, trackingData),
    [onAddFirstRow, trackingData],
  );

  return (
    <StyledWrapper>
      <StyledTopBar>
        <SettingsSearchInput value={searchValue} onChange={(value) => setSearchValue(value ?? '')} />
        <ButtonGroup>
          <Button variant={ButtonVariantsEnum.LINK} onClick={() => onExportCsv()} leftIcon="downloadSmall">
            <Trans id="settings-rule-editor-table-export-csv">Export CSV</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.LINK} onClick={() => onImportCsv()} leftIcon="exportSmall">
            <Trans id="settings-rule-editor-table-import-csv">Import CSV</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={onAddRowWithTracking}
            leftIcon="plusCircleSmall"
          >
            <Trans id="settings-rule-editor-table-add-row">Add row</Trans>
          </Button>
        </ButtonGroup>
      </StyledTopBar>

      <DataFieldsTable
        {...tableActions}
        onAddRow={onAddRowWithTracking}
        onAddColumn={onAddColumnWithTracking}
        columns={table.columns}
        rows={table.rows}
        columnValidator={columnValidator}
        emptyState={emptyState}
        searchText={searchValue || undefined}
      />
    </StyledWrapper>
  );
};

/*
 * Helpers.
 */

function makeEmptyState(onAddRow: Handler, trackingData?: Record<string, unknown>) {
  return (
    <StyledEmptyStateWrapper>
      <img alt="" src={ruleEditorEmptyTableIllustration} />
      <StyledEmptyStateTitle>
        <Trans id="settings-rule-editor-table-empty-state-title">Get set to scale!</Trans>
      </StyledEmptyStateTitle>
      <StyledEmptyStateDescription>
        <Trans id="settings-rule-editor-table-empty-state-explanation">
          Add and group rules that share similar conditions with rule sets. Use this table to store data
          variables referenced by the rule set&apos;s shared conditions. Each row of data represents a single
          rule.
        </Trans>
      </StyledEmptyStateDescription>
      <StyledEmptyStateButtonsWrapper>
        <LearnMoreButton
          variant={ButtonVariantsEnum.LINK}
          helpPageUrl=""
          onClick={() =>
            trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_RULE_TABLE_LEARN_MORE_CLICK, trackingData)
          }
        />
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onAddRow} leftIcon="plusCircleSmall">
          <Trans id="settings-rule-editor-table-empty-state-add-row">Add row</Trans>
        </Button>
      </StyledEmptyStateButtonsWrapper>
    </StyledEmptyStateWrapper>
  );
}

export function makeDefaultRuleEditorDataFieldsTable(): DataFieldsTableModel {
  return {
    columns: [
      {
        id: brandDataFieldsTableColumnId('1'),
        name: 'Matching column',
        type: DataFieldsTableColumnTypesEnum.STRING,
      },
      {
        id: brandDataFieldsTableColumnId('2'),
        name: 'Value column',
        type: DataFieldsTableColumnTypesEnum.STRING,
      },
    ],
    rows: [],
  };
}

function exportCsv(table: DataFieldsTableModel) {
  const csvText = generateCsvText(table);

  downloadBlob(`rule-table-export-${new Date().toISOString()}.csv`, new Blob([csvText], {type: 'text/csv'}));
}

function generateCsvText(table: DataFieldsTableModel) {
  const result = [];

  const makeRow = (row: DataFieldsTableData) =>
    table.columns.map((col) => row[col.id]).map(formatStringForCsv);

  result.push(table.columns.map((col) => formatStringForCsv(col.name)).join(','));
  for (const row of table.rows) {
    result.push(makeRow(row).join(','));
  }

  return result.join('\n');
}

// The following function is only meant for testing / beta.
// We will move to the classic job system later.
function importCsv(updateTable: HandlerOf<DataFieldsTableUpdater>) {
  const input = document.createElement('input');
  input.type = 'file';

  input.onchange = async () => {
    const files = Array.from(input.files ?? []);
    if (!files.length) {
      return;
    }
    const csvText = await files[0].text();
    const [columns, ...rows] = csvText.split('\n');
    const parsedColumns = columns.split(',').map(
      (column, index): DataFieldsTableColumnModel => ({
        id: brandDataFieldsTableColumnId(`${index}`),
        name: column,
        type: DataFieldsTableColumnTypesEnum.STRING,
      }),
    );

    const table: DataFieldsTableModel = {
      columns: parsedColumns,
      rows: rows
        .map((row) => row.split(','))
        .filter((row) => row.length === parsedColumns.length)
        .map((row) => Object.fromEntries(row.map((value, index) => [parsedColumns[index].id, value]))),
    };

    updateTable(() => table);
  };

  input.click();
}
